import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import type { FieldRendererProps, FieldDisplayProps } from '../../config/types';
import { Text } from '@/components/ui/Text';
import { cn } from '@/lib/cn';
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  isValidPhoneNumber,
  AsYouType,
  type CountryCode,
} from 'libphonenumber-js';

const DEFAULT_COUNTRY: CountryCode = 'US';
const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
const SUPPORTED_COUNTRIES = new Set<string>(getCountries());

/** Validate a runtime-context country hint (ISO-3166 alpha-2) to a CountryCode. */
function asCountryCode(value: string | undefined): CountryCode | undefined {
  const code = value?.toUpperCase();
  return code && SUPPORTED_COUNTRIES.has(code) ? (code as CountryCode) : undefined;
}

/** ISO-3166 alpha-2 → emoji flag (regional-indicator code points). */
function flagOf(code: string): string {
  return code.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

interface Country {
  code: CountryCode;
  name: string;
  dial: string;
}

/** All countries (name + dial code), computed once, sorted by name. */
const COUNTRIES: Country[] = getCountries()
  .map((code) => ({ code, name: regionNames.of(code) ?? code, dial: getCountryCallingCode(code) }))
  .sort((a, b) => a.name.localeCompare(b.name));

/**
 * Derive {country, national} from a stored E.164 value (for edit/restore). When
 * there is no stored value, fall back to `fallback` — the form's country (ENT-350)
 * — so a fresh field defaults to the engagement's jurisdiction, not a hardcoded US.
 */
function parseValue(
  value: string,
  fallback: CountryCode = DEFAULT_COUNTRY,
): { country: CountryCode; national: string } {
  if (value) {
    const parsed = parsePhoneNumberFromString(value);
    if (parsed?.country) return { country: parsed.country, national: parsed.formatNational() };
  }
  return { country: fallback, national: '' };
}

/**
 * Phone field — a single unified control: a compact flag + dial-code country
 * picker (searchable popover) joined to a number input, with an inline validity
 * tick. Stores the value as an E.164 string (e.g. `+12025550123`); empty when no
 * number. Pair with the `phone` validation rule (see setup-registry).
 */
export function PhoneFieldRenderer({
  field,
  value,
  onChange,
  onBlur,
  error,
  runtimeContext,
}: FieldRendererProps) {
  // ENT-350: default the country selector to the FORM's country (e.g. an India
  // engagement → +91) rather than a hardcoded US, so a valid national number is
  // accepted without the user having to switch the country by hand. A stored
  // E.164 value still wins (edit/restore keeps its own country).
  const formCountry = asCountryCode(runtimeContext?.country) ?? DEFAULT_COUNTRY;
  const [country, setCountry] = useState<CountryCode>(
    () => parseValue((value as string) ?? '', formCountry).country,
  );
  const [national, setNational] = useState(
    () => parseValue((value as string) ?? '', formCountry).national,
  );
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const e164 = (value as string) ?? '';
  const isValid = !!e164 && isValidPhoneNumber(e164);

  function emit(nextCountry: CountryCode, nextNational: string) {
    const digits = nextNational.replace(/\D/g, '');
    if (!digits) {
      onChange('');
      return;
    }
    const parsed = parsePhoneNumberFromString(digits, nextCountry);
    onChange(parsed ? parsed.number : `+${getCountryCallingCode(nextCountry)}${digits}`);
  }

  // Close the picker on outside click / Escape; focus the search when it opens.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    searchRef.current?.focus();
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [query]);

  function pick(c: Country) {
    setCountry(c.code);
    emit(c.code, national);
    setOpen(false);
    setQuery('');
  }

  return (
    <div ref={rootRef} className="relative">
      <div
        className={cn(
          'flex h-9 items-stretch rounded-lg bg-surface-elevated transition-colors',
          'border border-input-border focus-within:border-primary focus-within:ring-1 focus-within:ring-primary',
          error && 'border-danger',
        )}
      >
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          disabled={field.isDisabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`Country code: ${regionNames.of(country) ?? country} (+${getCountryCallingCode(country)})`}
          className="flex items-center gap-1.5 rounded-l-lg pl-3 pr-2 text-sm text-text outline-none transition-colors hover:bg-surface-muted/40 focus-visible:bg-surface-muted/50 disabled:opacity-50"
        >
          <span className="text-base leading-none">{flagOf(country)}</span>
          <span className="tabular-nums text-text-muted">+{getCountryCallingCode(country)}</span>
          <ChevronDown size={13} className={cn('text-text-subtle transition-transform duration-200', open && 'rotate-180')} />
        </button>

        <span className="my-1.5 w-px bg-border/70" aria-hidden />

        {/*
          Deliberately a raw <input>, not ui/Input (W4.T5.8).

          This is one SEGMENT of a composite control: the country button, the divider and this
          field all sit inside a single bordered container that owns the border, background,
          radius and focus ring. ui/Input brings its own bg-surface-elevated, rounded and
          border-b, which would draw a second input box inside the first.
        */}
        <input
          id={field.id}
          type="tel"
          inputMode="tel"
          value={national}
          onChange={(e) => {
            const formatted = new AsYouType(country).input(e.target.value);
            setNational(formatted);
            emit(country, formatted);
          }}
          onBlur={onBlur}
          placeholder={field.placeholder ?? 'Phone number'}
          disabled={field.isDisabled}
          aria-invalid={!!error}
          className="min-w-0 flex-1 bg-transparent px-3 text-sm text-text outline-none placeholder:text-text-subtle disabled:opacity-50"
        />

        {isValid && <Check size={15} className="mr-3 shrink-0 self-center text-success" aria-label="Valid number" />}
      </div>

      {open && (
        <div className="absolute z-50 mt-1.5 w-[300px] overflow-hidden rounded-lg border border-border bg-surface-elevated shadow-ambient animate-scale-in">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search size={13} className="shrink-0 text-text-subtle" aria-hidden />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country or code…"
              aria-label="Search country"
              className="w-full bg-transparent text-sm text-text outline-none placeholder:text-text-subtle"
            />
          </div>
          <ul role="listbox" aria-label="Countries" className="max-h-60 overflow-y-auto py-1">
            {filtered.map((c) => {
              const active = c.code === country;
              return (
                <li key={c.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => pick(c)}
                    className={cn(
                      'flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm outline-none transition-colors',
                      active ? 'bg-primary-subtle text-primary' : 'text-text hover:bg-surface-muted/50 focus-visible:bg-surface-muted/50',
                    )}
                  >
                    <span className="text-base leading-none">{flagOf(c.code)}</span>
                    <span className="min-w-0 flex-1 truncate">{c.name}</span>
                    <span className="tabular-nums text-text-subtle">+{c.dial}</span>
                    {active && <Check size={14} className="shrink-0 text-primary" />}
                  </button>
                </li>
              );
            })}
            {filtered.length === 0 && (
              <li className="px-3 py-2.5">
                <Text variant="body-sm" color="muted">No matching country.</Text>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Review-step display: international format, or em-dash when empty. */
export function PhoneDisplay({ value }: FieldDisplayProps) {
  const v = (value as string) ?? '';
  const parsed = v ? parsePhoneNumberFromString(v) : null;
  return (
    <Text variant="body-sm" as="span">
      {parsed ? `${flagOf(parsed.country ?? '')} ${parsed.formatInternational()}`.trim() : v || '—'}
    </Text>
  );
}
